import React, { useState } from 'react';
import { 
  Users, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, HelpCircle, FileText 
} from 'lucide-react';
import { LocalModelToggle } from './LocalModelToggle';

interface HrComplianceProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація аналітичного модуля кадрового комплаєнсу...",
  "Кадровий аудитор розбирає норми КЗпП та готує проект договору чи наказу...",
  "Інспектор з кадрової безпеки перевіряє вимоги ДСТУ 4163:2020 та специфічні правила...",
  "Генерація остаточного оформленого кадрового документа..."
];

const LOADING_STEPS_EN = [
  "Initializing HR compliance AI agents...",
  "Labor auditor checks Ukrainian Labor Code (CLL) and drafts contract/policy...",
  "HR security inspector checks DSTU 4163:2020 layouts and internal terms...",
  "Compiling finalized compliant HR document..."
];

export default function HrCompliance({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: HrComplianceProps) {
  const isUk = language === 'uk';
  const [hrDocumentType, setHrDocumentType] = useState(isUk ? 'Трудовий договір' : 'Employment Contract');
  const [hrDetailsText, setHrDetailsText] = useState(
    useEnterpriseContext ? (isUk ? 'Кадрові реквізити та штатні дані нашої компанії будуть автоматично підтягнуті з бази знань кабінету.' : 'Company registration metadata and HR structure details will be loaded from the workspace knowledge base.') : ''
  );
  const [hrSpecificRulesText, setHrSpecificRulesText] = useState('');
  
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : true;
  });

  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  // Cycle loading steps
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  const handleRun = async () => {
    if (!hrDetailsText.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Конструктор та комплаєнс кадрових документів (HR)",
          agentId: "hr-compliance",
          useLocalModel,
          inputs: {
            hr_document_type: hrDocumentType,
            hr_details_text: hrDetailsText,
            hr_specific_rules_text: hrSpecificRulesText || (isUk ? "Немає особливих умов" : "No specific conditions")
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка розробки кадрового документа.' : 'Failed to generate HR document.'));
      }

      const data = await response.json();
      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report content.');
              }
              setReport(resText);
              setIsAuditing(false);
            } else if (statusData.status === 'failed') {
              setError(statusData.error || (isUk ? 'Помилка генерації документа.' : 'Document generation failed.'));
              setIsAuditing(false);
            } else {
              setTimeout(pollJob, 2000);
            }
          } catch (pollErr: any) {
            console.error(pollErr);
            setError(pollErr.message || (isUk ? 'Помилка опитування завдання.' : 'Polling error.'));
            setIsAuditing(false);
          }
        };
        setTimeout(pollJob, 1000);
      } else {
        setReport(data.report || data.result || '');
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = hrDocumentType.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_") + "_hr_draft";
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
              <span>{isUk ? "← До Бізнес Центру" : "← Back to Business Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6 text-emerald-500" />
              <span>{isUk ? "Кадровий комплаєнс & HR" : "AI HR & Labor Compliance"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk ? "Створення та комплаєнс кадрових документів відповідно до Кодексу законів про працю (КЗпП) України" : "Generates employment agreements and HR policies compliant with Ukrainian Labor laws"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main Form */}
      {!report && !isAuditing && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => {
              setHrDocumentType(isUk ? "Трудовий договір" : "Employment Contract");
              setHrDetailsText(`Співробітник: Коваленко Дмитро Сергійович.
Посада: Старший розробник ПЗ (Senior Software Engineer).
Режим роботи: Гібридний (3 дні офіс / 2 дні дистанційно). Випробувальний термін: 2 місяці.
Посадовий оклад: 85 000 грн. Оплачувана відпустка: 24 календарні дні.`);
              setHrSpecificRulesText("Угода про нерозголошення коду та комерційної таємниці (NDA). Ненормований робочий день із компенсацією додатковими днями відпустки.");
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Трудовий договір Senior Engineer)" : "⚡ Try 1-click Demo Sample (Employment Contract)"}</span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Тип кадрового документа" : "HR Document Type"}
                </label>
                <select
                  value={hrDocumentType}
                  onChange={(e) => setHrDocumentType(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-sm transition-colors text-slate-850"
                >
                  <option value={isUk ? "Трудовий договір" : "Employment Contract"}>{isUk ? "Трудовий договір" : "Employment Contract"}</option>
                  <option value={isUk ? "Посадова інструкція" : "Job Description"}>{isUk ? "Посадова інструкція" : "Job Description"}</option>
                  <option value={isUk ? "Наказ про прийняття на роботу" : "Hiring Order"}>{isUk ? "Наказ про прийняття на роботу" : "Hiring Order"}</option>
                  <option value={isUk ? "Заява про звільнення" : "Resignation Statement"}>{isUk ? "Заява про звільнення" : "Resignation Statement"}</option>
                  <option value={isUk ? "Додаткова угода до договору" : "Contract Amendment"}>{isUk ? "Додаткова угода до договору" : "Contract Amendment"}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Дані співробітника та посадові обов'язки" : "Employee details & responsibilities"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Вкажіть повне ім'я, посаду, посадові обов'язки співробітника, режим роботи (дистанційно, офіс), оклад, тривалість відпустки..." 
                    : "Enter full name, job title, responsibilities, working hours, salary amount, vacation days..."}
                  value={hrDetailsText}
                  onChange={(e) => setHrDetailsText(e.target.value)}
                  rows={8}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Додаткові внутрішні правила роботодавця (необов'язково)" : "Employer internal rules (optional)"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Вкажіть додаткові обмеження, випробувальний термін, умови конфіденційності (NDA), компенсації, правила користування технікою..." 
                    : "Enter probation period details, NDA terms, company computer usage regulations, compensations..."}
                  value={hrSpecificRulesText}
                  onChange={(e) => setHrSpecificRulesText(e.target.value)}
                  rows={4}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>
            </div>

            <button
              onClick={handleRun}
              disabled={!hrDetailsText.trim()}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-650 disabled:bg-slate-100 disabled:text-slate-400 text-white font-black text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <Users className="w-4 h-4" />
              <span>{isUk ? "Розробити кадровий документ" : "Generate HR Document"}</span>
            </button>
          </div>

          {/* Guidelines Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                <HelpCircle className="w-4 h-4 text-emerald-505" />
                <span>{isUk ? "Як це працює?" : "How it works?"}</span>
              </h3>
              <ul className="space-y-3 font-mono text-[10px] text-slate-650 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-650 font-bold rounded-full flex items-center justify-center shrink-0">1</span>
                  <span>{isUk ? "Кадровий юрист створює проект на основі статей КЗпП України." : "Labor compliance clerk drafts the text incorporating CLL articles."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-650 font-bold rounded-full flex items-center justify-center shrink-0">2</span>
                  <span>{isUk ? "Контролює випробувальний термін, умови праці та захист компанії." : "Checks probation legality, safety regulations, and employer protection."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-650 font-bold rounded-full flex items-center justify-center shrink-0">3</span>
                  <span>{isUk ? "Формує фінальну версію для швидкого друку за ДСТУ стандартом." : "Outputs DSTU-aligned formatting ready for corporate printing."}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading State */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-emerald-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-emerald-500 rounded-full animate-spin" />
            <Users className="w-6 h-6 text-emerald-500" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Кадровий аудит..." : "Compiling HR terms..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic leading-normal px-4">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка аналізу" : "Execution Error"}
            </h4>
            <p className="text-xs text-red-650 leading-relaxed font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Report View */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Action Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-emerald-50 p-1.5 rounded border border-emerald-100">
                <FileText className="w-4 h-4 text-emerald-650" />
              </div>
              <span className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                {isUk ? "Кадровий документ" : "Labor Compliance Document"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCopyReport}
                className="py-1.5 px-3 bg-white hover:bg-slate-50 text-slate-650 hover:text-slate-800 border border-slate-200 rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
              <button 
                onClick={handleDownloadWord}
                className="py-1.5 px-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Word (.docx)</span>
              </button>
            </div>
          </div>

          {/* Report Content */}
          <div className="p-6 sm:p-8 prose prose-slate max-w-none text-xs leading-relaxed font-mono bg-slate-50 border-b border-slate-100 overflow-x-auto whitespace-pre-wrap text-slate-800">
            {report}
          </div>
          
          <div className="p-4 bg-white flex justify-end">
            <button
              onClick={() => {
                setReport(null);
                setHrDetailsText('');
                setHrSpecificRulesText('');
              }}
              className="py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-mono text-[10px] uppercase rounded-lg tracking-wider cursor-pointer transition-colors"
            >
              {isUk ? "Новий документ" : "New Document"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
